import {
  Component,
  OnInit,
  ElementRef,
  ViewChild,
  HostListener,
  Input,
} from '@angular/core';
import { Firestore, collection, collectionData } from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { ActivatedRoute } from '@angular/router';

interface Category {
  id: string;
  name: string;
  capacityLabel: string;
  status: boolean;
  imageUrl: string;
}

@Component({
  selector: 'app-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
})
export class SearchBarComponent implements OnInit {
  /* -------------------------
     NEW INPUT FOR STYLE MODE
     ------------------------- */
  @Input() mode: 'landing' | 'navbar' = 'landing';

  @ViewChild('searchRow') searchRow!: ElementRef<HTMLDivElement>;

  // FORM VALUES
  whatValue: string = '';
  selectedEmirate: string | null = null;
  selectedStart: string | null = null;
  selectedEnd: string | null = null;

  // UI STATE
  activeField: string | null = null;
  showEmirates: boolean = false;
  showCategories: boolean = false;
  showCalendarFrom: boolean = false;
  showCalendarTo: boolean = false;

  // EMIRATES (same as landing page)
  emirates = [
    { name: 'Abu Dhabi', map: '../../../../assets/landingpage/abudhabi.png' },
    { name: 'Ajman', map: '../../../../assets/landingpage/ajman.png' },
    { name: 'Dubai', map: '../../../../assets/landingpage/dubai.png' },
    { name: 'Fujairah', map: '../../../../assets/landingpage/fuja.png' },
    { name: 'Ras Al Khaimah', map: '../../../../assets/landingpage/ras.png' },
    { name: 'Sharjah', map: '../../../../assets/landingpage/sharjah.png' },
    { name: 'Umm Al Quwain', map: '../../../../assets/landingpage/umm.png' },
  ];

  // CALENDAR
  currentDate: Date = new Date();
  currentMonth: number = this.currentDate.getMonth();
  currentYear: number = this.currentDate.getFullYear();

  weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  calendarDays: any[] = [];

  get currentMonthName() {
    return new Date(this.currentYear, this.currentMonth).toLocaleString(
      'default',
      { month: 'long' },
    );
  }

  // CATEGORIES
  categories$!: Observable<Category[]>;
  categoriesList: Category[] = [];

  get filteredCategories() {
    if (!this.whatValue.trim()) return this.categoriesList;
    return this.categoriesList.filter((cat) =>
      cat.name.toLowerCase().includes(this.whatValue.toLowerCase()),
    );
  }

  constructor(
    private afs: Firestore,
    private router: Router,
    private route: ActivatedRoute,
    public ux: UxserviceService,
  ) { }

  ngOnInit(): void {
    this.loadCategories();
    this.generateCalendar();

    // Populate search bar from query params
    this.route.queryParams.subscribe((params) => {
      this.whatValue = params['what'] || '';
      this.selectedEmirate = params['emirate'] || null;
      this.selectedStart = params['from'] || null;
      this.selectedEnd = params['to'] || null;
    });
  }

  /* -------------------------
     OUTSIDE CLICK HANDLER
     ------------------------- */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (this.searchRow && !this.searchRow.nativeElement.contains(target)) {
      // Clicked outside the search bar
      this.activeField = null;
      this.showEmirates = false;
      this.showCategories = false;
      this.showCalendarFrom = false;
      this.showCalendarTo = false;
    }
  }

  /* -------------------------
     ACTIVATION / FIELD FOCUS
     ------------------------- */
  activate(field: 'what' | 'where' | 'from' | 'to') {
    this.activeField = field;

    // Reset all popups
    this.showEmirates = false;
    this.showCategories = false;
    this.showCalendarFrom = false;
    this.showCalendarTo = false;

    if (field === 'where') {
      this.showEmirates = true;
    } else if (field === 'from') {
      this.showCalendarFrom = true;
    } else if (field === 'to') {
      this.showCalendarTo = true;
    } else if (field === 'what') {
      this.showCategories = true;
    }
  }

  selectCategory(category: Category) {
    this.whatValue = category.name;
    this.showCategories = false;
    this.activeField = null;
  }

  clearWhat(event: Event) {
    event.stopPropagation();
    this.whatValue = '';
    // Optional: focus back on input?
    // User expectation: clear and probably type something else?
    // Let's keep it simple for now.
  }

  /* -------------------------
     EMIRATE SELECTOR
     ------------------------- */
  chooseEmirate(name: string) {
    this.selectedEmirate = name;
    this.showEmirates = false;
  }

  /* -------------------------
     CALENDAR GENERATION
     ------------------------- */
  generateCalendar() {
    const days: any[] = [];

    const firstDay = new Date(this.currentYear, this.currentMonth, 1).getDay();
    const lastDate = new Date(
      this.currentYear,
      this.currentMonth + 1,
      0,
    ).getDate();

    const today = new Date();
    const todayDate = today.getDate();
    const todayMonth = today.getMonth();
    const todayYear = today.getFullYear();

    // Empty leading cells
    for (let i = 0; i < firstDay; i++) {
      days.push({ date: '', inactive: true, isToday: false, isPast: false });
    }

    // Actual days
    for (let d = 1; d <= lastDate; d++) {
      const isToday =
        this.currentYear === todayYear &&
        this.currentMonth === todayMonth &&
        d === todayDate;

      const isPast =
        this.currentYear < todayYear ||
        (this.currentYear === todayYear && this.currentMonth < todayMonth) ||
        (this.currentYear === todayYear &&
          this.currentMonth === todayMonth &&
          d < todayDate);

      days.push({
        date: d,
        inactive: false,
        isToday,
        isPast,
      });
    }

    this.calendarDays = days;
  }

  prevMonth() {
    this.currentMonth--;
    if (this.currentMonth < 0) {
      this.currentMonth = 11;
      this.currentYear--;
    }
    this.generateCalendar();
  }

  nextMonth() {
    this.currentMonth++;
    if (this.currentMonth > 11) {
      this.currentMonth = 0;
      this.currentYear++;
    }
    this.generateCalendar();
  }

  selectStart(day: any) {
    if (day.inactive || day.isPast) return;

    this.selectedStart = `${day.date}/${this.currentMonth + 1}/${this.currentYear}`;
    this.showCalendarFrom = false;
  }

  selectEnd(day: any) {
    if (day.inactive || day.isPast) return;

    this.selectedEnd = `${day.date}/${this.currentMonth + 1}/${this.currentYear}`;
    this.showCalendarTo = false;
  }

  /* -------------------------
     LOAD CATEGORIES
     ------------------------- */
  private loadCategories() {
    const categoryRef = collection(this.afs, 'vehicle-categories');
    const raw$ = collectionData(categoryRef, { idField: 'id' }) as Observable<
      Category[]
    >;

    this.categories$ = raw$.pipe(
      map((list) => list.filter((cat) => cat.status === true)),
    );

    this.categories$.subscribe((list) => {
      this.categoriesList = list;
    });
  }

  /* -------------------------
     SEARCH ACTION
     ------------------------- */
  emitSearch() {
    if (!this.whatValue.trim()) {
      this.ux.showError("Please enter what you're searching for.");
      return;
    }

    if (!this.selectedEmirate) {
      this.ux.showError('Please select an Emirate.');
      return;
    }

    const matchedCategory = this.categoriesList.find((cat) =>
      this.whatValue.toLowerCase().includes(cat.name.toLowerCase()),
    );

    const queryParams = {
      what: this.whatValue,
      emirate: this.selectedEmirate,
      from: this.selectedStart || '',
      to: this.selectedEnd || '',
    };

    // Navigate to correct category or ALL if not found
    const targetId = matchedCategory ? matchedCategory.id : 'all';

    this.router.navigate(['/category', targetId], { queryParams }).then(() => {
      // Reload if we are already on the same route but query params changed
      // (Angular sometimes doesn't reload component on query param change if reuse strategy is default,
      // but here we force reload just in case, though usually subscription handles it)
      // Actually, removing window.reload() is better SPA practice if subscription handles it.
      // But keeping existing behavior of reload if that was intended, OR just rely on subscription in target.
      // The previous code had window.location.reload().
      // Let's keep it consistent or improve it.
      // If we are already on /category/all, we want the component to pick up the changes.
      // The target component subscribes to route.queryParams, so a simple navigate should work.
      // However, to be 100% sure we trigger the "search" effect as per user expectation:
      // window.location.reload(); // Leaving this out for better SPA experience, unless critical.
    });
  }
}